// Shop Builder "Product Grid" block (node.type "shopProductGrid") options.
// Every option is optional: a block saved before they existed keeps the
// historical behaviour (platform pagination / infinite-scroll setting,
// newest first, the block's `limit` as page size).
export const SHOP_GRID_SORTS = Object.freeze({
  newest: Object.freeze({ sortBy: "createdAt", asc: false }),
  oldest: Object.freeze({ sortBy: "createdAt", asc: true }),
  priceAsc: Object.freeze({ sortBy: "price", asc: true }),
  priceDesc: Object.freeze({ sortBy: "price", asc: false }),
  rating: Object.freeze({ sortBy: "Stars", asc: false }),
  name: Object.freeze({ sortBy: "name", asc: true }),
});

export const DEFAULT_SHOP_SORT = SHOP_GRID_SORTS.newest;

// Same whitelist as server_elakay/elakay_helpers/lakayShopListing.js
// (LAKAY_SHOP_SORT_FIELDS); anything else falls back to newest first.
export const normalizeShopSort = (sort) => {
  const found = Object.values(SHOP_GRID_SORTS).find((entry) => entry.sortBy === sort?.sortBy && entry.asc === Boolean(sort?.asc));
  return found || DEFAULT_SHOP_SORT;
};

export const shopSortKeyOf = (sort) => {
  const normalized = normalizeShopSort(sort);
  return Object.keys(SHOP_GRID_SORTS).find((key) => SHOP_GRID_SORTS[key] === normalized) || "newest";
};

const clampCount = (value, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 1 ? Math.min(50, Math.round(number)) : fallback;
};

const clampColumns = (value, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 1 ? Math.min(6, Math.round(number)) : fallback;
};

export const DEFAULT_SHOP_GRID_COLUMNS = Object.freeze({ xs: 2, sm: 2, md: 3, lg: 4 });

export const normalizeShopGridOptions = (options = {}) => {
  const limit = clampCount(options.limit, 12);
  const columns = options.columns && typeof options.columns === "object" ? options.columns : {};
  return {
    layout: ["auto", "grid", "list"].includes(options.layout) ? options.layout : "auto",
    loadingMode: ["inherit", "pagination", "infinite"].includes(options.loadingMode) ? options.loadingMode : "inherit",
    initialCount: clampCount(options.initialCount, limit),
    batchSize: clampCount(options.batchSize, limit),
    sortKey: Object.prototype.hasOwnProperty.call(SHOP_GRID_SORTS, options.sortKey) ? options.sortKey : "newest",
    columns: {
      xs: clampColumns(columns.xs, DEFAULT_SHOP_GRID_COLUMNS.xs),
      sm: clampColumns(columns.sm, DEFAULT_SHOP_GRID_COLUMNS.sm),
      md: clampColumns(columns.md, DEFAULT_SHOP_GRID_COLUMNS.md),
      lg: clampColumns(columns.lg, DEFAULT_SHOP_GRID_COLUMNS.lg),
    },
  };
};

// True when the block only uses the historical options: it then renders
// through the unchanged legacy path.
export const isLegacyShopGridConfig = (config) => (
  config.loadingMode === "inherit" && config.sortKey === "newest" && config.layout === "auto"
);

// Builder previews on local sample data (no owner): same orders locally.
export const sortShopProductsLocally = (products, sort) => {
  const list = Array.isArray(products) ? [...products] : [];
  const { sortBy, asc } = normalizeShopSort(sort);
  if (sortBy === "createdAt" && !asc) return list;
  const value = (product) => {
    const raw = product?.[sortBy];
    if (sortBy === "name") return String(Array.isArray(raw) ? raw[0] : raw || "").toLowerCase();
    if (sortBy === "createdAt") return new Date(raw || 0).getTime() || 0;
    return Number(raw) || 0;
  };
  return list
    .map((product, index) => ({ product, index }))
    .sort((a, b) => {
      const va = value(a.product);
      const vb = value(b.product);
      if (va < vb) return asc ? -1 : 1;
      if (va > vb) return asc ? 1 : -1;
      return a.index - b.index;
    })
    .map(({ product }) => product);
};
